import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import { STORAGE_KEYS } from "../utils/constants";

const PAGE_SIZE = 20; // matches denzo-api's own admin/v1/products/list page size

// Cache is scoped to the "full catalog" (small-store) menu source only — the
// paginated path never holds the complete list in memory, so there's nothing
// clean to persist for it. storeId guards against a different store's login
// on the same device/browser reading back the wrong menu.
function readMenuCache(storeId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.menu);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.storeId !== storeId || !Array.isArray(parsed.products)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeMenuCache(storeId, products, categories) {
  try {
    localStorage.setItem(STORAGE_KEYS.menu, JSON.stringify({ storeId, products, categories, cachedAt: Date.now() }));
  } catch {
    // Storage full/unavailable (private browsing, quota) — the cache is a
    // nice-to-have, never worth failing the menu load over.
  }
}

// The menu, loaded from pos/v1/products — POSController@getProducts, which
// serves the products and category list straight off the same two cache
// entries initPOS uses — or, for the full-catalog path, from this hook's own
// localStorage cache when one exists (see loadFromNetwork/readMenuCache
// below), refetched only when the waiter taps reload. This is deliberately
// NOT pos/v1/init: init also drags the store, the user and their permissions,
// reminders, table areas and both order lists along, none of which change
// between tab presses. init runs once per login instead — see
// AuthContext.ensureSession.
//
// Two very different product sources, unified behind one "Load More" interface:
//
// - Small stores: pos/v1/products returns the FULL catalog in one shot — that
//   path has no pagination at all, so there's nothing to page over the wire.
//   "Load More" here just reveals more of an already-loaded (and already
//   search/category-filtered) array.
// - Medium/large stores: pos/v1/products returns products: [] for these, and we
//   fall back to admin/v1/products/list, which IS server-paginated
//   (ProductController@listProducts ...->paginate($this->page_limit), and
//   also accepts `search`/`category[]` query params) — "Load More" there is a
//   real network call for the next page, and changing search/category
//   re-queries the server from page 1 instead of filtering a local array.
// pos/v1/products is newer than the rest of this app's API surface, so a
// frontend deployed ahead of denzo-api answers 404 there. Fall back to init in
// that one case: it returns the same products and categories, just with the
// store, user, reminders, table areas and both order lists dragged along, which
// is exactly the waste this endpoint exists to avoid — but a heavy menu beats
// no menu. Safe to delete once the backend is deployed everywhere.
async function fetchMenu() {
  try {
    return (await apiClient.get("products")).data;
  } catch (err) {
    if (err?.status !== 404) throw err;
    return (await apiClient.get("init")).data;
  }
}

export default function useMenu({ search = "", category = "all" } = {}) {
  const { session, store } = useAuth();
  const storeId = store?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  // Seeded from the categories the login bootstrap cached, so the category
  // picker is populated on first paint instead of after the fetch lands.
  const [categories, setCategories] = useState(() => session?.categories || []);
  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const sourceRef = useRef(null); // 'full' | 'paginated'
  const allProductsRef = useRef([]); // full catalog, small-store path only
  const visibleCountRef = useRef(PAGE_SIZE);
  const pageRef = useRef(1);

  const filterLocalProducts = useCallback(() => {
    const term = search.trim().toLowerCase();
    return allProductsRef.current.filter((p) => {
      if (category !== "all" && p.category_id !== category) return false;
      if (term && !p.name?.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [search, category]);

  const applyLocalWindow = useCallback(() => {
    const filtered = filterLocalProducts();
    setProducts(filtered.slice(0, visibleCountRef.current));
    setHasMore(filtered.length > visibleCountRef.current);
  }, [filterLocalProducts]);

  const fetchPaginatedPage = useCallback(async (page, append) => {
    const adminBase = (process.env.REACT_APP_API_BASE_URL || "").replace("/pos/v1", "/admin/v1");
    // axios skips baseURL whenever the given url is already absolute, so this
    // reaches admin/v1 directly even though apiClient's own baseURL is pos/v1.
    const res = await apiClient.get(`${adminBase}products/list`, {
      params: { page, search, category: category !== "all" ? [category] : undefined },
    });
    const paginator = res.data?.products || {};
    const newItems = paginator.data || [];
    setProducts((prev) => (append ? [...prev, ...newItems] : newItems));
    setHasMore((paginator.current_page || 1) < (paginator.last_page || 1));
    pageRef.current = page;
  }, [search, category]);

  // Learns which product source this store uses. Pulled out of the mount
  // effect below so the reload button can re-run exactly this on demand, not
  // just once at mount.
  const loadFromNetwork = useCallback(() => {
    setLoading(true);
    setError("");
    return fetchMenu()
      .then(async (data) => {
        // Only overwrite the seeded list when the response actually carries
        // categories; a user without 'list categories' gets [] back and should
        // keep whatever the bootstrap already gave the picker.
        if ((data.categories || []).length) setCategories(data.categories);

        if ((data.products || []).length) {
          sourceRef.current = "full";
          allProductsRef.current = data.products;
          visibleCountRef.current = PAGE_SIZE;
          applyLocalWindow();
          // Cache only the full-catalog shape — the paginated path below never
          // holds the complete list in memory to cache.
          writeMenuCache(storeId, data.products, data.categories || []);
        } else {
          sourceRef.current = "paginated";
          await fetchPaginatedPage(1, false);
        }
      })
      .catch((err) => setError(err.message || "Failed to load menu"))
      .finally(() => setLoading(false));
  }, [applyLocalWindow, fetchPaginatedPage, storeId]);

  // Initial load: cache-first for the full-catalog path, so a returning
  // waiter gets the menu painted instantly with no network wait, and only
  // refetches when they explicitly tap reload — instead of every mount
  // re-fetching the whole catalog over again. No cache yet (first run on this
  // device, or a paginated/medium-large store) falls back to the network.
  useEffect(() => {
    const cached = readMenuCache(storeId);
    if (cached) {
      sourceRef.current = "full";
      allProductsRef.current = cached.products;
      visibleCountRef.current = PAGE_SIZE;
      if (cached.categories?.length) setCategories(cached.categories);
      applyLocalWindow();
      setLoading(false);
      return;
    }
    loadFromNetwork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // React to search/category changes once we know which source we're using.
  useEffect(() => {
    if (!sourceRef.current) return;
    if (sourceRef.current === "full") {
      visibleCountRef.current = PAGE_SIZE;
      applyLocalWindow();
    } else {
      setLoadingMore(false);
      setLoading(true);
      fetchPaginatedPage(1, false)
        .catch((err) => setError(err.message || "Failed to load menu"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category]);

  const loadMoreProducts = useCallback(() => {
    if (sourceRef.current === "full") {
      visibleCountRef.current += PAGE_SIZE;
      applyLocalWindow();
      return;
    }
    setLoadingMore(true);
    fetchPaginatedPage(pageRef.current + 1, true)
      .catch((err) => setError(err.message || "Failed to load menu"))
      .finally(() => setLoadingMore(false));
  }, [applyLocalWindow, fetchPaginatedPage]);

  return { loading, loadingMore, error, categories, products, hasMore, loadMoreProducts, reload: loadFromNetwork };
}
